import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { MarketingShell } from "@/components/site/marketing-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, MessageSquare, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

export const SUPPORT_EMAIL = "wclifhkfjlokfshntrkhm@gmail.com";

export const Route = createFileRoute("/help")({
  head: () => ({
    meta: [
      { title: "Support Center — CryptoBull Pro" },
      { name: "description", content: "Get help, contact CryptoBull Pro support, or send us a message." },
    ],
  }),
  component: HelpPage,
});

const contactSchema = z.object({
  full_name: z.string().trim().min(1, "Required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  subject: z.string().trim().min(1, "Required").max(150),
  message: z.string().trim().min(5, "Tell us a bit more").max(2000),
});

function HelpPage() {
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const parsed = contactSchema.safeParse({
      full_name: fd.get("full_name"),
      email: fd.get("email"),
      subject: fd.get("subject"),
      message: fd.get("message"),
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSubmitting(true);
    // Compose a mailto so the user's email client opens with the message ready to send.
    const { full_name, email, subject, message } = parsed.data;
    const body = `From: ${full_name} <${email}>\n\n${message}`;
    const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Your email app is opening — send the message to reach support.");
      (e.currentTarget as HTMLFormElement).reset();
    }, 400);
  };

  return (
    <MarketingShell>
      <section className="mx-auto max-w-5xl px-4 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary mb-4">
            <LifeBuoy className="h-6 w-6" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold">Support Center</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl mx-auto">
            We're here to help. Email us directly or send a message using the form below.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-10">
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="glass rounded-2xl p-6 hover:bg-white/[0.03] transition-colors"
          >
            <Mail className="h-5 w-5 text-primary mb-3" />
            <div className="text-sm font-semibold">Email support</div>
            <div className="text-xs text-muted-foreground mt-1 break-all">{SUPPORT_EMAIL}</div>
          </a>
          <div className="glass rounded-2xl p-6">
            <MessageSquare className="h-5 w-5 text-primary mb-3" />
            <div className="text-sm font-semibold">Live AI assistant</div>
            <div className="text-xs text-muted-foreground mt-1">
              Tap the chat button at the bottom right for instant answers.
            </div>
          </div>
          <div className="glass rounded-2xl p-6">
            <LifeBuoy className="h-5 w-5 text-primary mb-3" />
            <div className="text-sm font-semibold">Account tickets</div>
            <div className="text-xs text-muted-foreground mt-1">
              Signed-in users can open a tracked support ticket from the dashboard.
            </div>
          </div>
        </div>

        <form className="glass rounded-3xl p-7 grid gap-4 max-w-2xl mx-auto" onSubmit={onSubmit}>
          <h2 className="text-lg font-semibold">Contact us</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Full name</Label>
              <Input name="full_name" required maxLength={100} />
            </div>
            <div className="space-y-2">
              <Label>Email address</Label>
              <Input type="email" name="email" required maxLength={255} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input name="subject" required maxLength={150} />
          </div>
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea name="message" required rows={6} maxLength={2000} />
          </div>
          <Button type="submit" disabled={submitting}>
            {submitting ? "Opening…" : "Send message"}
          </Button>
          <p className="text-[11px] text-muted-foreground">
            Submitting opens your email client pre-filled with your message. Or email us directly at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">{SUPPORT_EMAIL}</a>.
          </p>
        </form>
      </section>
    </MarketingShell>
  );
}